/*
 * Created Date: 08-10-2021, 10:03:37 am
 * Author: Trần Quang Hùng
 * Email: you@you.you
 * -----
 * Last Modified:
 * Modified By:
 * -----
 * Copyright (c) 2021 VNE E-COMMERCE, Inc
 * -----
 * HISTORY:
 * Date      	By	Comments
 * ----------	---	----------------------------------------------------------
 */

import { errorMsg, handleErrorFieldBackend } from '@Core/Helpers/Message'
import { generateGUID } from '@Core/Helpers/String'
import { useBreakpoints } from '@Core/hooks/useBreakpoints'
import CloseIcon from '@mui/icons-material/Close'
import { LoadingButton } from '@mui/lab'
import {
	Autocomplete,
	Button,
	Chip,
	CircularProgress,
	Dialog,
	DialogContent,
	DialogTitle,
	Divider,
	FormHelperText,
	IconButton,
	InputLabel,
	Paper,
	Slide,
	TextField,
	Typography
} from '@mui/material'
import { useRequest } from 'ahooks'
import clsx from 'clsx'
import { find, get, isObject, map } from 'lodash'
import PropTypes from 'prop-types'
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import CoreInput, { useStyles } from './CoreInput'

const CoreAutocomplete = ({
	className,
	control,
	name,
	options,
	label,
	placeholder,
	InputLabelProps,
	inputProps,
	InputProps,
	shrink,
	required,
	readOnly,
	fetchOptions,
	filter,
	valuePath,
	labelPath,
	loading,
	isCacheOption,
	returnValueType,
	multiple,
	disabled,
	isCacheKey,
	cacheKey,
	helperText,
	isCreateable,
	AutoCompleteClassName,
	rules,
	size,
	mobileMode,
	showAdd,
	addDataKey,
	addMode,
	labelType,
	...restProps
}) => {
	const { t } = useTranslation()
	const customStyle = useStyles()
	const { mobile } = useBreakpoints()
	const [openMobileSelect, setOpenMobileSelect] = useState(false)

	const handleOpenMobileSelect = () => {
		if (mobile && mobileMode) {
			setOpenMobileSelect(true)
		}
	}

	const handleCloseMobileSelect = useCallback(() => {
		setOpenMobileSelect(false)
	}, [])

	const responsiveSize = () => {
		if (String(size)?.trim()?.length > 0) {
			return size
		}
		return mobile ? 'small' : 'medium'
	}

	const {
		data: fetchedOptions = options,
		run: handleFetchOptions,
		loading: fetching
	} = useRequest(fetchOptions, {
		manual: true
	})

	const [customAddData, setCustomAddedData] = useState([])

	const filterdOptions = useMemo(() => {
		if (!fetchedOptions) return options.concat(customAddData)

		if (filter) return filter(fetchedOptions).concat(customAddData)
		return fetchedOptions.concat(customAddData)
	}, [fetchedOptions, filter, t, JSON.stringify(customAddData)])

	const checkHasCurrentOptions = () => {
		// TODO
		// check cached data and return it
		return null
	}

	useEffect(() => {
		if (fetchOptions && !disabled && !readOnly) {
			if (isCacheOption) {
				if (!checkHasCurrentOptions()) {
					handleFetchOptions()
				}
			} else {
				handleFetchOptions()
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [fetchOptions, readOnly, disabled])

	const getValueOption = useCallback(
		value => {
			if (multiple) {
				if (isCreateable) {
					return value
				}
				const values = map(value, v => {
					if (!isObject(v)) {
						const option =
							find(filterdOptions, item => {
								return get(item, valuePath) === v
							}) ?? null
						return option
					}
					return v
				}).filter(Boolean)
				return values
			}

			if (returnValueType === 'enum') {
				return find(filterdOptions, item => get(item, valuePath) === value) ?? null
			}

			return value
		},
		[filterdOptions, t]
	)

	return (
		<div className={className}>
			<Controller
				control={control}
				name={name}
				render={({ field: { onChange, onBlur, value, ref }, fieldState: { error } }) => {
					return (
						<>
							<Autocomplete
								size={responsiveSize()}
								label={labelType === 'shrink' ? label : null}
								className={clsx(AutoCompleteClassName, customStyle.root)}
								multiple={multiple}
								isOptionEqualToValue={(option, value) => {
									if (value instanceof Object) {
										return get(option, valuePath) === get(value, valuePath)
									}
									return get(option, valuePath) === value
								}}
								getOptionLabel={option => {
									return get(option, labelPath) ?? ''
								}}
								loading={loading || fetching}
								options={filterdOptions}
								noOptionsText={t('No option available')}
								disabled={disabled}
								onChange={(_, value) => {
									if (value === null) {
										return onChange(null)
									}
									return returnValueType === 'enum'
										? onChange(multiple ? value.map(v => get(v, valuePath)) : get(value, valuePath))
										: onChange(value)
								}}
								onBlur={onBlur}
								// onInputChange={(e, newInput) => console.log('============= newInput', newInput)}
								value={getValueOption(value)}
								renderOption={(props, option) => (
									<li {...props} key={option[valuePath] ?? generateGUID()}>
										{get(option, labelPath)}
									</li>
								)}
								// eslint-disable-next-line react/no-unstable-nested-components
								PaperComponent={({ children, ...rest }) => (
									<Paper {...rest} className={clsx({ hidden: mobile && mobileMode })}>
										{children}
									</Paper>
								)}
								renderInput={params => (
									<>
										{label && labelType !== 'shrink' ? (
											<InputLabel
												className="text-[1rem] md:text-12 font-medium text-label overflow-auto"
												required={required}
												shrink
												htmlFor={name}
											>
												{label}
											</InputLabel>
										) : null}
										<TextField
											onClick={handleOpenMobileSelect}
											{...params}
											label={null}
											placeholder={placeholder || t('Chọn {{label}}', { label })}
											inputRef={ref}
											error={!!error}
											helperText={error && error.message}
											InputLabelProps={{
												...params.InputLabelProps,
												shrink: true,
												required,
												...InputLabelProps
											}}
											inputProps={{
												...params.inputProps,
												readOnly,
												...inputProps
											}}
											// eslint-disable-next-line react/jsx-no-duplicate-props
											InputProps={{
												...params.InputProps,
												endAdornment: (
													<>
														{loading || fetching ? (
															<CircularProgress color="inherit" size={20} />
														) : null}
														{params.InputProps.endAdornment}
													</>
												),
												...InputProps
											}}
										/>
										{helperText && <FormHelperText>{helperText}</FormHelperText>}
									</>
								)}
								{...restProps}
							/>
							{mobileMode && mobile ? (
								<MobileSelectDialog
									open={openMobileSelect}
									handleClose={handleCloseMobileSelect}
									label={label}
									showAdd={showAdd}
									addDataKey={addDataKey}
									t={t}
									value={value}
									setValue={onChange}
									addMode={addMode}
									setCustomAddedData={setCustomAddedData}
									options={filterdOptions}
									control={control}
									name={name}
									error={error}
								/>
							) : null}
						</>
					)
				}}
				rules={rules}
			/>
		</div>
	)
}

CoreAutocomplete.defaultProps = {
	className: null,
	options: [],
	label: null,
	placeholder: null,
	InputLabelProps: null,
	inputProps: null,
	InputProps: null,
	required: false,
	readOnly: false,
	fetchOptions: undefined,
	filter: undefined,
	valuePath: 'value',
	labelPath: 'label',
	isCacheOption: false,
	returnValueType: 'option',
	isCacheKey: true,
	cacheKey: undefined,
	isCreateable: false,
	mobileMode: false,
	addDataKey: '',
	showAdd: false,
	addMode: ''
}

CoreAutocomplete.propTypes = {
	className: PropTypes.string,
	control: PropTypes.object.isRequired,
	name: PropTypes.string.isRequired,
	options: PropTypes.array,
	label: PropTypes.string,
	placeholder: PropTypes.string,
	InputLabelProps: PropTypes.object,
	inputProps: PropTypes.object,
	InputProps: PropTypes.object,
	required: PropTypes.bool,
	readOnly: PropTypes.bool,
	fetchOptions: PropTypes.func,
	filter: PropTypes.func,
	disableClearable: PropTypes.bool,
	disabled: PropTypes.bool,
	isCacheOption: PropTypes.bool,
	valuePath: PropTypes.string,
	labelPath: PropTypes.string,
	returnValueType: PropTypes.oneOf(['option', 'enum']),
	multiple: PropTypes.bool,
	isCacheKey: PropTypes.bool,
	cacheKey: PropTypes.string,
	helperText: PropTypes.any,
	filterOptions: PropTypes.func,
	getOptionLabel: PropTypes.func,
	isCreateable: PropTypes.bool,
	AutoCompleteClassName: PropTypes.string,
	rules: PropTypes.object,
	mobileMode: PropTypes.bool,
	addDataKey: PropTypes.string,
	showAdd: PropTypes.bool,
	addMode: PropTypes.string
}

export default React.memo(CoreAutocomplete)

const Transition = React.forwardRef(function Transition(props, ref) {
	return <Slide direction="up" ref={ref} {...props} />
})

export const MobileSelectDialog = memo(props => {
	const { open, options, handleClose, label, setValue, value, error, showAdd, t, control, name, ...restProps } = props

	const handleValueChange = itemValue => {
		console.log(value)
		const isExisted = value?.includes(value)
		setValue(isExisted ? value?.filter(item => item !== itemValue) : [...value, itemValue], {
			shouldDirty: true,
			shouldValidate: true
		})
	}

	const isChecked = itemValue => {
		if (value?.includes(itemValue)) {
			return true
		}
		return false
	}

	return (
		<Dialog
			open={open}
			TransitionComponent={Transition}
			keepMounted
			onClose={handleClose}
			className="w-full h-full"
			PaperProps={{
				className: 'w-full h-auto m-0 max-h-[100vh] absolute bottom-0 rounded-t-3xl'
			}}
		>
			<DialogTitle variant="M14">
				<Typography component="p" className="text-center">
					{label}
				</Typography>
			</DialogTitle>
			<IconButton className="absolute right-2 top-2" onClick={handleClose}>
				<CloseIcon className="text-mainBlue" />
			</IconButton>
			<Divider />
			<DialogContent className="flex flex-col gap-2 p-4">
				{showAdd ? <FormAdd {...restProps} setValue={setValue} value={value} /> : null}
				<div className="flex flex-wrap items-center w-full gap-3">
					{options.map((item, index) => (
						<Chip
							className="min-w-[60px]"
							key={index}
							label={item.label}
							color={isChecked(item?.value) ? 'primary' : 'default'}
							onClick={() => handleValueChange(item.value)}
							disabled={item?.disabled ?? false}
						/>
					))}
				</div>

				{error?.message ? <FormHelperText error>{error.message}</FormHelperText> : null}
				{options?.length === 0 && <Typography variant="helper">{t('No options avaiable')}</Typography>}
			</DialogContent>

			<Button onClick={handleClose} variant="main-gradient" size="small" className="m-6 w-max">
				{t('Confirm')}
			</Button>
		</Dialog>
	)
})

export const FormAdd = props => {
	const [showForm, setShowForm] = useState(false)
	const { t } = useTranslation()
	const { addDataKey, setValue, value, addMode, setCustomAddedData } = props
	// console.log('🚀 ~ FormAdd ~ props:', props)

	const {
		handleSubmit,
		control,
		setError,
		reset,
		formState: { isSubmitting, isDirty }
	} = useForm({
		mode: 'all',
		defaultValues: {
			[addDataKey]: ''
		}
	})

	const handleSubmitAdd = handleSubmit(async data => {
		if (addMode === 'static') {
			setCustomAddedData(prev => [...prev, { label: data[addDataKey], value: data[addDataKey] }])
			setValue([...value, data[addDataKey]])
			setShowForm(false)
			reset()
			return true
		}

		if (addMode === 'api') {
			try {
				// do something
				setShowForm(false)
				reset()
			} catch (error) {
				errorMsg()
				handleErrorFieldBackend(error, setError)
			}
		}
	})

	const handlekeyDown = e => {
		if (e?.key === 'Enter') {
			e.preventDefault()
			handleSubmitAdd()
		}
	}

	return (
		<>
			{!showForm && (
				<Chip
					label={` + ${t('Tạo mới')}`}
					color="default"
					className="mb-2 w-max"
					onClick={e => {
						setShowForm(true)
					}}
				/>
			)}

			{showForm ? (
				// eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
				<form onSubmit={handleSubmitAdd} className="flex w-full gap-2 " onKeyDown={handlekeyDown}>
					<CoreInput
						className="w-full"
						size="small"
						name={addDataKey}
						control={control}
						placeholder={t('placeholder.enter_value')}
					/>
					<Button
						// type="submit"
						onClick={handleSubmitAdd}
						size="medium"
						variant="secondary-gradient"
						loading={isSubmitting}
						// disabled={!isDirty}
					>
						{t('Create')}
					</Button>
				</form>
			) : null}
		</>
	)
}
